// Notices under the checkout delivery postcode (DEV-51047). Warnings only:
// none of them ever stops the order.
import { AlertTriangle } from 'lucide-react'
import { distanceInMiles } from '../../../utils/distance'
import type { DeliveryPostcodeStatus } from '../../../hooks/useDeliveryPostcodeCheck'
import type { PostcodeCoordinates } from '../../../services/postcodes'

// Beyond this straight-line distance from the job location, ask the engineer
// to double-check the delivery address (catches real-but-wrong postcodes).
const FAR_DELIVERY_MILES = 5

export interface DeliveryBranchChange {
  supplierName: string
  oldBranchName: string
  newBranchName: string
}

interface DeliveryPostcodeNoticesProps {
  /** Postcode stock and dates were checked for (the header pill). */
  workingPostcode: string
  /** What's in the delivery postcode box. */
  deliveryPostcode: string
  status: DeliveryPostcodeStatus
  /** Tidy spelling once the delivery postcode is found. */
  canonicalPostcode: string | null
  /** Job location from the session (postcode centre or device position). */
  jobCoordinates: PostcodeCoordinates | null
  /** Delivery postcode centre; only known when found via postcodes.io. */
  deliveryCoordinates: PostcodeCoordinates | null
  /**
   * Suppliers whose fulfilment branch changes under the delivery postcode
   * (DEV-52059). One notice row per entry; empty = no change / nothing to warn.
   */
  branchChanges?: DeliveryBranchChange[]
  /**
   * True when the delivery-postcode stock lookup for CPS/Wolseley failed.
   * Shows a single fallback notice so the engineer knows the displayed branch
   * may not match what the supplier uses on fulfilment.
   */
  branchLookupFailed?: boolean
}

function Notice({ children }: { children: string }) {
  return (
    <p className="flex items-start gap-1.5 text-amber-700 text-xs mt-1">
      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
      <span>{children}</span>
    </p>
  )
}

export function DeliveryPostcodeNotices({
  workingPostcode,
  deliveryPostcode,
  status,
  canonicalPostcode,
  jobCoordinates,
  deliveryCoordinates,
  branchChanges,
  branchLookupFailed,
}: DeliveryPostcodeNoticesProps) {
  // 'idle' means empty or the same as the working postcode; 'checking' and
  // 'not_found' would flash half-typed or wrong postcodes, so only a settled
  // different postcode gets the "checked for" notice (AC2).
  const isSettledDifferent = status === 'found' || status === 'unchecked'
  const shownDelivery = canonicalPostcode ?? deliveryPostcode.trim().toUpperCase()
  // No distance warning without both positions; it's a nice-to-have.
  const milesAway =
    status === 'found' && jobCoordinates && deliveryCoordinates
      ? distanceInMiles(jobCoordinates, deliveryCoordinates)
      : null

  return (
    <>
      {status === 'unchecked' && (
        <Notice>We couldn't check this postcode right now. Please make sure it's right.</Notice>
      )}
      {isSettledDifferent && (
        <Notice>{`Stock and dates were checked for ${workingPostcode}, not ${shownDelivery}.`}</Notice>
      )}
      {milesAway !== null && milesAway > FAR_DELIVERY_MILES && (
        <Notice>
          {`This delivery postcode is ${milesAway.toFixed(1)} miles from your job location (${workingPostcode}). Please double-check the address.`}
        </Notice>
      )}
      {branchChanges?.map((change) => (
        <Notice key={`${change.supplierName}-${change.oldBranchName}-${change.newBranchName}`}>
          {`Delivery branch will be ${change.newBranchName}, not ${change.oldBranchName}.`}
        </Notice>
      ))}
      {branchLookupFailed && (
        <Notice>
          We couldn't check the delivery branch right now. The branch shown may change when the supplier fulfils your order.
        </Notice>
      )}
    </>
  )
}
